import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Loader2 } from "lucide-react";

/**
 * OAuthCallback
 *
 * Handles the Supabase OAuth redirect for Google (and any other provider).
 * Supabase stores the session after the redirect, then we:
 *  1. Wait for the session.
 *  2. Check if the user has completed onboarding (target_language set).
 *  3. Route accordingly.
 */
const OAuthCallback: React.FC = () => {
  const nav = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const cleanUrl = () => {
      if (window.location.hash || window.location.search) {
        window.history.replaceState(null, document.title, window.location.pathname);
      }
    };

    const routeForSession = async (session: NonNullable<Awaited<ReturnType<typeof supabase.auth.getSession>>["data"]["session"]>) => {
      // Check if they already completed onboarding. ProtectedRoute uses target_language,
      // so keep this callback aligned with the real onboarding-complete signal.
      const { data: profile, error: profileError } = await supabase
        .from("users")
        .select("target_language")
        .eq("id", session.user.id)
        .maybeSingle();

      if (cancelled) return;
      if (profileError) throw profileError;

      if (profile?.target_language) {
        nav("/dashboard", { replace: true });
        return;
      }

      if (!profile) {
        await supabase.from("users").insert({
          id: session.user.id,
          target_language: "",
          native_language: "",
          learning_goal: "",
          cefr_level: "A1",
          claimed_cefr_level: null,
          effective_cefr_level: "A1",
          placement_status: "needs_onboarding",
          last_assessed_at: null,
          assessment_completed_at: null,
          streak: 0,
        });
      }

      if (!cancelled) nav("/onboarding", { replace: true });
    };

    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");

        // Remove tokens from the visible URL immediately after reading them.
        cleanUrl();

        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (sessionError) throw sessionError;
        }

        const { data, error: getSessionError } = await supabase.auth.getSession();
        if (getSessionError) throw getSessionError;
        if (data.session) {
          await routeForSession(data.session);
          return;
        }

        // Fallback for the rare race where Supabase is still finishing URL detection.
        const session = await new Promise<typeof data.session>((resolve) => {
          let authSubscription: { unsubscribe: () => void } | null = null;
          const timeout = window.setTimeout(() => {
            authSubscription?.unsubscribe();
            resolve(null);
          }, 5000);

          const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            if (!nextSession) return;
            window.clearTimeout(timeout);
            authSubscription?.unsubscribe();
            resolve(nextSession);
          });
          authSubscription = sub.subscription;
        });

        if (!session) throw new Error("Could not complete sign-in. Please try again.");
        await routeForSession(session);
      } catch (err: any) {
        if (!cancelled) {
          cleanUrl();
          setError(err?.message || "Could not complete sign-in. Please try again.");
        }
      }
    };

    handleCallback();

    return () => {
      cancelled = true;
    };
  }, [nav]);

  return (
    <div className="min-h-screen bg-base flex flex-col items-center justify-center gap-3 text-whisper px-4">
      {!error ? (
        <>
          <Loader2 size={18} className="animate-spin text-aura-cyan" />
          <span className="font-mono text-[12px] tracking-[0.22em] uppercase">Signing you in...</span>
        </>
      ) : (
        <>
          <p className="max-w-sm text-center text-[14px] text-red-300">{error}</p>
          <button
            type="button"
            onClick={() => nav("/auth", { replace: true })}
            className="mt-2 rounded-full border border-panel/15 px-4 py-2 text-[13px] text-ink hover:border-aura-cyan/40"
          >
            Back to sign in
          </button>
        </>
      )}
    </div>
  );
};

export default OAuthCallback;
